import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../stores/store';
import ListLayout from '../../../Generic/ListLayout';
import DataTable from '../../../Generic/DataTable';
import confirmDeleteDialog from '../../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../../hooks/useDeleteItem';
import useFilterData from '../../../../hooks/useFilterData';
import useFetchData from '../../../../hooks/useFetchData';
import useLocalizedTextsMap from '../../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../../hooks/useSetMenu";
import { createActiveColumn, createActionColumn, createGeneralColumn, createBoolColumn } from '../../../Generic/dataGridColumns';

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { mainStore, systemVersionStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  useSetMenu(mainStore.SystemVersionsAdminMenuType, mainStore.SystemVersionsAdminMenuNo);
  const [columns, setColumns] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  useFetchData(axiosPrivate, `/SystemVersions`, systemVersionStore);
  const filteredData = useFilterData(toJS(systemVersionStore.systemVersions), searchQuery);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const deleteItem = useDeleteItem(axiosPrivate, '/SystemVersions', systemVersionStore.deleteItem);

  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteItem, id);
  }, [deleteItem, t]);

  const cols = useMemo(() => [
    createGeneralColumn("versionOrder", "order", t, 80),
    createGeneralColumn("versionLabel", "version", t, 100),
    createGeneralColumn("nameEN", "name", t, 150),
    createBoolColumn("isCurrent", "current", t, 100),
    createActiveColumn(t),
    createActionColumn(t, "/Admin/SystemVersions/Edit", remove),
  ].filter(Boolean), [t, remove]);

  useEffect(() => {
    setColumns(cols);
    // eslint-disable-next-line
  }, [t]);

  return (
    <>
      <ListLayout
        title={t("systemVersionsListTitle") || "System Versions"}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("searchPlaceholder")}
        addButtonLink="/Admin/SystemVersions/Add"
        addButtonTitle={t("add")}
      >
        <DataTable
          rows={filteredData}
          columns={columns}
          pageSize={pageSize}
          setPageSize={setPageSize}
          localeTextsMap={localizedTextsMap}
        />
      </ListLayout>
    </>
  );
};

export default observer(List);
